import {
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, fromEvent, Observable, Subject, takeUntil } from 'rxjs';
import {
  FilterByOptions,
  PaymentStatus,
  SurveyInstallationReopenStatus,
  SurveyInstallationStatus,
} from 'src/app/models/survey.model';
import { KeyValueItem } from 'src/app/models/keyValueItem.model';
import { SurveyDetails } from 'src/app/models/survey.model';
import { SurveyService } from 'src/app/services/survey.service';
import { TranslationService } from 'src/app/services/translation.service';
import { Region } from 'src/app/models/region.model';
import { Area } from 'src/app/models/area.model';
import { ChecklistItem } from 'src/app/models/checklist-item.model';
import { RegionService } from 'src/app/services/region.service';
import { AreaService } from 'src/app/services/area.service';
import { TariffService } from 'src/app/services/tariff.service';
import { Tariff } from 'src/app/models/tariff.model';
import {
  CustomerType,
  MeterStatus,
  TypeOfConnection,
} from 'src/app/models/customer.model';
import { DueDateFilterOptions } from 'src/app/enums/due-date-filter-options.enum';
import { PermissionsEnum } from 'src/app/models/role.model';
import { AuthService, GeographicalRegion, GeographicalRegionService, PermissionsService, SubGeographicalRegion, SubGeographicalRegionService } from 'shared-lib';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { OrganizationTeamApiService } from 'src/app/services/organization-team-api.service';

@Component({
  selector: 'app-survey',
  templateUrl: './survey.component.html',
  styleUrls: ['./survey.component.scss'],
})
export class SurveyComponent implements OnInit {
  public PermissionsEnum = PermissionsEnum;
  surveys: SurveyDetails[] = [];
  stats: KeyValueItem[] = [];
  public detailsShowed: boolean = false;

  mapSortBy: Record<number, string> = {
    [FilterByOptions.ALL]: 'COMMON.FILTER_BY_OPTIONS.ALL',
    [FilterByOptions.WALL]: 'COMMON.FILTER_BY_OPTIONS.WALL',
    [FilterByOptions.POLE]: 'COMMON.FILTER_BY_OPTIONS.POLE',
  };

  @Input() dateFrom: string;
  @Input() dateTo: string;

  count: number = 2;

  //tab counts
  countAssigned: number = 0;
  countRejected: number = 0;
  countApproved: number = 0;
  countSubmited: number = 0;

  pageSize: number = 10;
  currentPage: number = 1;
  currentTab: number = 4;
  currentTabName: string = 'Assigned';

  searchText: string = '';
  searchTextUpdate = new Subject<string>();

  filterByValue: FilterByOptions = FilterByOptions.ALL;
  filterByOptions = FilterByOptions;
  filterByName: string = this.mapSortBy[FilterByOptions.ALL];
  buName: string = this.translationService.getByKeyFromStorage('BU');
  utName: string = this.translationService.getByKeyFromStorage('UT');
  customerType: number = 0;
  customerTypeOptions = [
    { name: 'Prepaid', value: CustomerType.Prepaid, translation: 'COMMON.CUSTOMER_TYPE_OPTIONS.PREPAID' },
    { name: 'Postpaid', value: CustomerType.Postpaid, translation: 'COMMON.CUSTOMER_TYPE_OPTIONS.POSTPAID' },
  ];
  customerPhase: number = -1;
  customerPhaseOptions = [
    { name: 'Single Phase', value: TypeOfConnection.SinglePhase, translation: 'COMMON.CUSTOMER.PHASE_OPTIONS.SINGLE' },
    { name: 'Three Phase', value: TypeOfConnection.ThreePhase, translation: 'COMMON.CUSTOMER.PHASE_OPTIONS.THREE' },
  ];
  meterStatus: number = 0;
  meterStatusOptions = [
    { name: 'Unmetered', value: MeterStatus.Unmetered, translation: 'CUSTOMER_DETAILS.UNMETERED' },
    { name: 'Metered', value: MeterStatus.Metered, translation: 'CUSTOMER_DETAILS.METERED' },
  ];
  surveyStatus: number = 0;
  surveyStatusOptions = [
    { name: 'Submited', value: SurveyInstallationStatus.Submitted, translation: 'COMMON.ASSET_STATUS.SUBMITTED' },
    { name: 'Unvalidated', value: SurveyInstallationStatus.Unvalidated, translation: 'COMMON.ASSET_STATUS.UNVALIDATED' },
  ];
  dueDateFilter: number = -1;
  dueDateFilterOptions = [
    { name: 'Not Due', value: DueDateFilterOptions.NotDue, translation: 'COMMON.DUE_DATE_FILTER_OPTIONS.NOT_DUE' },
    { name: 'Due', value: DueDateFilterOptions.Due, translation: 'COMMON.DUE_DATE_FILTER_OPTIONS.DUE' },
    { name: 'Overdue', value: DueDateFilterOptions.Overdue, translation: 'COMMON.DUE_DATE_FILTER_OPTIONS.OVERDUE' },
  ];
  paymentStatus: number = 0;
  paymentStatusOptions = [
    { name: 'Not Paid', value: -1, translation: 'COMMON.PAYMENT_STATUS.NOT_PAID' },
    { name: 'Success', value: PaymentStatus.Success, translation: 'COMMON.PAYMENT_STATUS.SUCCESS' },
    { name: 'Pending', value: PaymentStatus.Pending, translation: 'COMMON.PAYMENT_STATUS.PENDING' },
    { name: 'Failed', value: PaymentStatus.Failed, translation: 'COMMON.PAYMENT_STATUS.FAILED' },
    { name: 'Ready For Payout', value: PaymentStatus.ReadyForPayout, translation: 'COMMON.PAYMENT_STATUS.READYFORPAYOUT' },
    { name: 'Refunded', value: PaymentStatus.Refunded, translation: 'COMMON.PAYMENT_STATUS.REFUNDED' },
    { name: 'Unknown', value: PaymentStatus.Unknown, translation: 'COMMON.PAYMENT_STATUS.UNKNOWN' },
    { name: 'Free Metering', value: PaymentStatus.FreeMetering, translation: 'COMMON.PAYMENT_STATUS.FREEMETERING' },
    { name: 'Aborted', value: PaymentStatus.Aborted, translation: 'COMMON.PAYMENT_STATUS.ABORTED' },
  ];
  reopenStatus: number = 0;
  reopenStatusOptions = [
    { name: 'Initial', value: SurveyInstallationReopenStatus.Initial, translation: 'SURVEYS.REOPEN_STATUS.INITIAL' },
    { name: 'Reopen', value: SurveyInstallationReopenStatus.Reopen, translation: 'SURVEYS.REOPEN_STATUS.REOPEN' },
  ];
  searchByOptions = [
    {
      name: 'AccountNumber',
      value: 'Account No.',
      translation: 'CUSTOMERS.SEARCH_BY.ACCOUNT_NO',
    },
    { name: 'Address', value: 'Address', translation: 'COMMON.ADDRESS' },
    {
      name: 'MeterNumber',
      value: 'Meter No.',
      translation: 'CUSTOMERS.SEARCH_BY.METER_NO',
    },
    {
      name: 'ContractNumber',
      value: 'Contract Number',
      translation: 'COMMON.CONTRACT_NUMBER',
    },
    {
      name: 'MapId',
      value: 'Map Id',
      translation: 'SURVEYS.MAP_ID',
    }
  ];
  selectedSearchTypeDisplay: string;
  showSelectSearchType: boolean = false;
  selectedSearchType: string;
  searchFilterApplied: boolean = false;
  showUpload: boolean = false;
  showFilterBy: boolean = false;
  clickObservable: Observable<Event> = fromEvent(document, 'click');
  private ngUnsubscribe = new Subject<void>();
  regions: Region[] = [];
  areas: Area[] = [];
  allAreas: Area[] = [];
  geographicalRegions: (GeographicalRegion & { checked?: boolean })[] = [];
  allSubGeographicalRegions: (SubGeographicalRegion & { checked?: boolean })[] = [];
  subGeographicalRegions: (SubGeographicalRegion & { checked?: boolean })[] = [];
  selectedSubGeographicalRegions: number[] = [];
  clicked: boolean = false;
  tariff: number = 0;
  tariffs: Tariff[] = [];
  selectedFilters: ChecklistItem[] = [];
  utilityId: number = 0;
  selectedAreas: number[] = [];
  selectedRegions: number[] = [];
  reset: boolean = false;
  organizations: any[] = [];
  selectedOrganizationId: number = 0;

  hasCimDataIntegration: boolean = false;

  constructor(
    private surveyService: SurveyService,
    private toastr: ToastrService,
    private translationService: TranslationService,
    private regionService: RegionService,
    private areaService: AreaService,
    public permissionsService: PermissionsService,
    private tariffService: TariffService,
    private analyticsService: AnalyticsService,
    private organizationService: OrganizationTeamApiService,
    private authService: AuthService,
    private geographicalRegionService: GeographicalRegionService,
    private subGeographicalRegionService: SubGeographicalRegionService,
  ) {
       this.hasCimDataIntegration = this.authService.hasCimDataIntegration;

  }

  ngOnInit(): void {
    this.utilityId = parseInt(localStorage.getItem('utilityId') || '');

    this.subscribeToClickEvent();
    this.getSurveysStats();
    if (this.hasCimDataIntegration) {
      this.getGeographicalRegions();
      this.getSubGeographicalRegions();
    } else {
      this.getRegions();
      this.getAreas();
    }
    this.getTariffs();
    this.getOrganizations();
    this.setFilters();
    this.getData();
    this.searchTextUpdate.pipe(debounceTime(500)).subscribe((_) => {
      if (this.searchText == '') this.search();
      this.searchFilterApplied = false;
    });
     this.selectedSearchType =
      this.selectedSearchType ?? this.searchByOptions[0].name;
        this.selectedSearchTypeDisplay =
      this.selectedSearchTypeDisplay ?? this.searchByOptions[0].translation;
  }

  ngOnDestroy(): void {
    this.searchTextUpdate.complete();
  }
  private getData(): void {
    this.reloadTable();
  }
  selectSearchType(searchType: any) {
    if (this.searchText && this.searchFilterApplied) {
      this.searchText = '';
      this.search();
    }
    this.selectedSearchType = searchType.name;
    this.selectedSearchTypeDisplay = searchType.translation;
    this.showSelectSearchType = false;
  }
  private getAll(obj: any) {
    this.surveyService.getAll(obj).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.surveys = responseData.data.data;
          this.count = responseData.data.count ?? this.count;
          if (responseData.message != '') {
            this.toastr.warning(responseData.message);
          }
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => this.toastr.error('Error occured'),
    });
  }

  getTimelineStatus(dueDate: string): { days: number; overdue: boolean } | null {
    if (!dueDate) return null;
    const due = new Date(dueDate);
    if (isNaN(due.getTime())) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);

    const diffDays = Math.round(
      (due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    );

    return diffDays >= 0
      ? { days: diffDays, overdue: false }
      : { days: Math.abs(diffDays), overdue: true };
  }

  datesValid() {
    if (this.dateFrom && this.dateTo) {
      const valid: boolean = new Date(this.dateFrom).getTime() < new Date(`${this.dateTo.toString()}T23:59:59`).getTime();
      return valid;
    }
    return true;
  }

  isAllCheckBoxChecked() {
    return false;
  }

  //stats cards
  private getSurveysStats() {
    this.surveyService.getSurveyStats().subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 200 || data?.status === 'OK') {
          data.data.forEach((element) => {
            if (element.key == 'Rejected') {
              this.countRejected = element.value;
              data.data = data.data.filter((item) => item != element);
            } else if (element.key == 'Submited') {
              this.countSubmited = element.value;
              data.data = data.data.filter((item) => item != element);
            } else if (element.key == 'Approved') {
              this.countApproved = element.value;
              data.data = data.data.filter((item) => item != element);
            } else if (element.key == 'Assigned') {
              this.countAssigned = element.value;
              data.data = data.data.filter((item) => item != element);
            }
          });
          this.stats = data.data;
        } else {
          this.toastr.error(data?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }
  getFilterObject() {
    var statuses: number[] = this.setFilterStatuses();

    let obj = {
      filterByInstallationType: this.filterByValue,
      search: this.searchText,
      filterBy: this.filterByValue,
      dateFrom: this.dateFrom ? this.dateFrom : null,
      dateTo: this.dateTo ? `${this.dateTo}T23:59:59` : null,
      status: this.currentTab,
      selectedAreas: this.selectedAreas,
      selectedRegions: this.selectedRegions,
      selectedGeographicalRegions: this.geographicalRegions.filter((x) => x.checked).map((x) => x.id),
      selectedSubgeographicalRegions: this.selectedSubGeographicalRegions,
      customerType: this.customerType,
      customerPhase: this.customerPhase,
      meterStatus: this.meterStatus,
      tariffId: this.tariff,
      surveyStatus: statuses,
      searchType: this.selectedSearchType,
      organizationId: this.selectedOrganizationId,
      paymentStatus: this.paymentStatus,
      dueDateFilter: this.dueDateFilter == -1 ? null : this.dueDateFilter,
      reopenStatus: this.reopenStatus == 0 ? null : this.reopenStatus,
    };
    return obj;
  }

  private getOrganizations() {
    this.organizationService.getAllForSelect().subscribe({
      next: (response) => {
        const responseData = response.body ?? response;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.organizations = responseData.data;
        }
      },
      error: (_) => { }
    });
  }

  selectMapVendor(e: any) {
    this.selectedOrganizationId = +e.target.value;
    this.createFilterCards();
    this.reloadTable(1);
  }
  checkAllCheckBox(event: any) { }

  onFilterBy(option: any) {
    if (option === 'New surveys')
      this.changeTab(1);
    else if (option === 'Approved surveys')
      this.changeTab(2);
    else if (option === 'Rejected surveys')
      this.changeTab(3);
  }

  reloadTable(page: any = null) {
    if (page) this.currentPage = page;
    let obj = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
        count: this.count
      },
      filterParams: this.getFilterObject()
    };

    this.analyticsService.logCustomEvent('SURVEY: Filter list', {
      page: obj.pageInfo.page,
      search: obj.filterParams.search,
      date_from: obj.filterParams.dateFrom,
      date_to: obj.filterParams.dateTo,
      status: obj.filterParams.status,
      customer_type: obj.filterParams.customerType,
      areas: obj.filterParams.selectedAreas,
      regions: obj.filterParams.selectedRegions,
      tariff: obj.filterParams.tariffId,
      customer_phase: obj.filterParams.customerPhase,
      meter_status: obj.filterParams.meterStatus,
      filter_by_installation_type: obj.filterParams.filterByInstallationType,
      tab: this.currentTabName
    });

    this.getAll(obj);
  }
  setFilterStatuses(): number[] {
    var array: number[] = [];
    switch (this.currentTab) {
      case 1:
        if (this.surveyStatus != 0) {
          array = [this.surveyStatus];
        } else {
          array = [1, 6];
        }
        break;
      case 2:
        array = [2];
        break;
      case 3:
        array = [3];
        break;
      case 4:
        array = [4, 5];
        break;
    }
    return array;
  }
  search() {
    this.searchFilterApplied = this.searchText ? true : false;
    this.reloadTable(1);
  }
  setFilterStorage() {
    let obj = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
        count: this.count
      },
      filterParams: this.getFilterObject(),
    };

    // Remove 'T23:59:59' from the dateTo to avoid errors
    if (obj.filterParams.dateTo) {
      obj.filterParams.dateTo = obj.filterParams.dateTo.replace('T23:59:59', '');
    }

    localStorage.setItem(
      'filter',
      JSON.stringify({ type: 'Survey', filter: obj })
    );
  }
  filterByType(option: FilterByOptions) {
    this.filterByValue = option;
    this.filterByName = this.mapSortBy[option];
    this.reloadTable();
  }

  pageChange(value: any) {
    this.currentPage = value;
    this.reloadTable();
  }

  private subscribeToClickEvent() {
    this.clickObservable
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((e: any) => {
        this.showUpload = false;
        this.showFilterBy = false;
      });
  }

  download() {
    let obj = this.getFilterObject();
    this.surveyService.download(obj).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.toastr.success('File downloaded');
          window.location.href = responseData.data;
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => this.toastr.error('Error occured'),
    });
  }
  openCreateModal() {
    this.detailsShowed = !this.detailsShowed;
  }

  changeTab(page: number) {
    this.currentTab = page;
    if (this.currentTab == 4) {
      this.currentTabName = 'Assigned';
    } else if (this.currentTab == 3) {
      this.currentTabName = 'Rejected';
    } else if (this.currentTab == 2) {
      this.currentTabName = 'Approved';
    } else if (this.currentTab == 1) {
      this.currentTabName = 'Submitted';
    }

    this.searchText = '';
    this.resetFilter(1);
    this.pageChange(1);
  }
  private getRegions() {
    this.regionService.getAllForSelect().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.regions = responseData.data;
          this.setCheckedRegions();
          this.createFilterCards();
          this.removeFilterStorage();
        } else if (response?.status == 204) {
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  private getAreas() {
    this.areaService.getAllForSelect().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.allAreas = responseData.data;
          this.setCheckedAreas();
          this.createFilterCards();
          this.removeFilterStorage();
        } else if (response?.status == 204) {
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  private getGeographicalRegions() {
    this.geographicalRegionService.getAllForSelect().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.geographicalRegions = responseData.data;
          this.setCheckedGeographicalRegions();
          this.createFilterCards();
          this.removeFilterStorage();
        } else if (response?.status == 204) {
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  private getSubGeographicalRegions() {
    this.subGeographicalRegionService.getAllForMappings().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.allSubGeographicalRegions = responseData.data;
          this.setCheckedSubGeographicalRegions();
          this.createFilterCards();
          this.removeFilterStorage();
        } else if (response?.status == 204) {
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  getTariffs() {
    this.tariffService.getAllForSelect().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.tariffs = responseData.data;
          this.createFilterCards();
          this.removeFilterStorage();
        } else if (response?.status == 204) {
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  selectRegion(e: any) {
    //list of areas for selected regions
    this.areas = this.allAreas.filter(area => this.regions.some(region => region.id == area.regionId && region.checked));
    this.allAreas.forEach(area => {
      if (!this.areas.some(a => a.id === area.id)) {
        area.checked = false; //uncheck areas for unchecked regions
      }
    });

    this.createFilterCards();
  }

  selectArea(e: any) {
    this.createFilterCards();
  }
  selectGeographicalRegion(e: any) {
    //list of sub geographical regions for selected geographical regions
    this.subGeographicalRegions = this.allSubGeographicalRegions.filter(sub => this.geographicalRegions.some(region => region.id == sub.geographicalRegion.id && region.checked));
    this.allSubGeographicalRegions.forEach(sub => {
      if (!this.subGeographicalRegions.some(s => s.id === sub.id)) {
        sub.checked = false; //uncheck sub geographical regions for unchecked geographical regions
      }
    });

    this.createFilterCards();
  }
  selectTariff(e: any) {
    this.tariff = +e.target.value;
    this.createFilterCards();
    this.reloadTable(1);
  }
  selectCustomerType(e: any) {
    this.customerType = +e.target.value;
    this.createFilterCards();
    this.reloadTable(1);
  }
  selectCustomerPhase(e: any) {
    this.customerPhase = +e.target.value;
    this.createFilterCards();
    this.reloadTable(1);
  }
  selectMeterStatus(e: any) {
    this.meterStatus = +e.target.value;
    this.createFilterCards();
    this.reloadTable(1);
  }
  selectSurveyStatus(e: any) {
    this.surveyStatus = +e.target.value;
    this.createFilterCards();
    this.reloadTable(1);
  }
  selectPaymentStatus(e: any) {
    this.paymentStatus = +e.target.value;
    this.createFilterCards();
    this.reloadTable(1);
  }
  selectDueDateFilter(e: any) {
    this.dueDateFilter = +e.target.value;
    this.createFilterCards();
    this.reloadTable(1);
  }
  selectReopenStatus(e: any) {
    this.reopenStatus = +e.target.value;
    this.createFilterCards();
    this.reloadTable(1);
  }
  removeFilter(item: ChecklistItem) {
    if (item.property == this.buName) {
      this.regions.forEach((region) => {
        if (region.name === item.selectedValue) {
          region.checked = false;
        }
      });
      this.clicked = !this.clicked;
    } else if (item.property == this.utName) {
      this.areas.forEach((area) => {
        if (area.name === item.selectedValue) {
          area.checked = false;
        }
      });
      this.clicked = !this.clicked;
    } else if (item.property == 'Geographical Region') {
      this.geographicalRegions.forEach((region) => {
        if (region.name === item.selectedValue) {
          region.checked = false;
        }
      });
      this.clicked = !this.clicked;
    } else if (item.property == 'Sub Geographical Region') {
      this.subGeographicalRegions.forEach((sub) => {
        if (sub.name === item.selectedValue) {
          sub.checked = false;
        }
      });
      this.clicked = !this.clicked;
    } else if (item.property == 'Tariff') {
      this.tariff = 0;
    } else if (item.property == 'Customer Type') {
      this.customerType = 0;
    } else if (item.property == 'Customer Phase') {
      this.customerPhase = -1;
    } else if (item.property == 'Meter Status') {
      this.meterStatus = 0;
    } else if (item.property == 'Survey Status') {
      this.surveyStatus = 0;
    } else if (item.property == 'MAP Vendor') {
      this.selectedOrganizationId = 0;
    } else if (item.property == 'Payment Status') {
      this.paymentStatus = 0;
    } else if (item.property == 'Due Date') {
      this.dueDateFilter = -1;
    } else if (item.property == 'Request Instance') {
      this.reopenStatus = 0;
    }

    this.createFilterCards();
    this.reloadTable(1);
  }

  resetFilter(e: any) {
    this.selectedFilters = [];

    this.regions.forEach((region) => {
      region.checked = false;
    });

    this.areas.forEach((area) => {
      area.checked = false;
    });
    this.areas = [];

    this.geographicalRegions.forEach((region) => {
      region.checked = false;
    });

    this.subGeographicalRegions.forEach((sub) => {
      sub.checked = false;
    });
    this.subGeographicalRegions = [];
    this.selectedSubGeographicalRegions = [];

    this.tariff = 0;
    this.customerPhase = -1;
    this.customerType = 0;
    this.meterStatus = 0;
    this.clicked = !this.clicked;
    this.surveyStatus = 0;
    this.selectedOrganizationId = 0;
    this.paymentStatus = 0;
    this.dueDateFilter = -1;
    this.reopenStatus = 0;
    this.reloadTable(1);
  }

  createFilterCards() {
    this.selectedFilters = [];

    let selectedAreas = this.areas.filter((x) => x.checked);
    let selectedRegions = this.regions.filter((x) => x.checked);
    this.selectedAreas = this.areas.filter((x) => x.checked).map((x) => x.id);
    this.selectedRegions = this.regions.filter((x) => x.checked).map((x) => x.id);

    let selectedGeographicalRegions = this.geographicalRegions.filter((x) => x.checked);
    let selectedSubGeographicalRegions = this.subGeographicalRegions.filter((x) => x.checked);
    this.selectedSubGeographicalRegions = selectedSubGeographicalRegions.map((x) => x.id as number);
    //regions
    for (var i = 0; i < selectedRegions.length; i++) {
      this.selectedFilters.push(
        new ChecklistItem(this.buName, selectedRegions[i].name, 'COMMON.BU.SINGLE')
      );
    }

    //areas
    for (var i = 0; i < selectedAreas.length; i++) {
      this.selectedFilters.push(
        new ChecklistItem(this.utName, selectedAreas[i].name, 'COMMON.UT.SINGLE')
      );
    }

    //geographical regions
    for (var i = 0; i < selectedGeographicalRegions.length; i++) {
      this.selectedFilters.push(
        new ChecklistItem('Geographical Region', selectedGeographicalRegions[i].name, 'COMMON.GEOGRAPHICAL_REGION.SINGLE')
      );
    }

    //sub geographical regions
    for (var i = 0; i < selectedSubGeographicalRegions.length; i++) {
      this.selectedFilters.push(
        new ChecklistItem('Sub Geographical Region', selectedSubGeographicalRegions[i].name, 'COMMON.SUB_GEOGRAPHICAL_REGION.SINGLE')
      );
    }

    //tariff
    if (this.tariff != 0) {
      let tariffName = this.tariffs.filter((x) => x.id == this.tariff)[0].name;
      this.selectedFilters.push(new ChecklistItem('Tariff', tariffName, 'COMMON.TARIFF.SINGLE'));
    }

    if (this.customerType != 0) {
      let type = this.customerTypeOptions.filter(
        (x) => x.value == this.customerType
      )[0];
      this.selectedFilters.push(new ChecklistItem('Customer Type', type.name, 'COMMON.MBC_DETAILS.CUSTOMER_TYPE', type.translation));
    }
    if (this.customerPhase != -1) {
      let phase = this.customerPhaseOptions.filter(
        (x) => x.value == this.customerPhase
      )[0];
      this.selectedFilters.push(new ChecklistItem('Customer Phase', phase.name, 'COMMON.CUSTOMER.CUSTOMER_PHASE', phase.translation));
    }
    if (this.meterStatus != 0) {
      let status = this.meterStatusOptions.filter(
        (x) => x.value == this.meterStatus
      )[0];
      this.selectedFilters.push(new ChecklistItem('Meter Status', status.name, 'COMMON.MBC_DETAILS.METER_STATUS', status.translation));
    }
    if (this.surveyStatus != 0) {
      let status = this.surveyStatusOptions.filter(
        (x) => x.value == this.surveyStatus
      )[0];
      this.selectedFilters.push(new ChecklistItem('Survey Status', status.name, 'SURVEYS.STATUS', status.translation));
    }
    if (this.selectedOrganizationId != 0) {
      let org = this.organizations.find((x) => x.id == this.selectedOrganizationId);
      if (org) {
        this.selectedFilters.push(new ChecklistItem('MAP Vendor', org.name, 'SURVEYS.MAP_VENDOR'));
      }
    }
    if (this.paymentStatus != 0) {
      let status = this.paymentStatusOptions.find((x) => x.value == this.paymentStatus);
      if (status) {
        this.selectedFilters.push(new ChecklistItem('Payment Status', status.name, 'COMMON.PAYMENT_STATUS.SINGLE', status.translation));
      }
    }
    if (this.dueDateFilter != -1) {
      let option = this.dueDateFilterOptions.find((x) => x.value == this.dueDateFilter);
      if (option) {
        this.selectedFilters.push(new ChecklistItem('Due Date', option.name, 'COMMON.DATE_TYPE.DUE_DATE', option.translation));
      }
    }
    if (this.reopenStatus != 0) {
      let option = this.reopenStatusOptions.find((x) => x.value == this.reopenStatus);
      if (option) {
        this.selectedFilters.push(new ChecklistItem('Request Instance', option.name, 'SURVEYS.REQUEST_INSTANCE', option.translation));
      }
    }
  }
  setCheckedAreas() {
    var filter = localStorage.getItem('filter');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'Survey') return;
    if (filterObject.filter.filterParams.selectedAreas) {
      this.selectedAreas = filterObject.filter.filterParams.selectedAreas;
      this.areas.forEach((element) => {
        if (this.selectedAreas.includes(element.id)) element.checked = true;
        else element.checked = false;
      });
    }
  }
  setCheckedRegions() {
    var filter = localStorage.getItem('filter');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'Survey') return;
    if (filterObject.filter.filterParams.selectedRegions) {
      this.selectedRegions = filterObject.filter.filterParams.selectedRegions;
      this.regions.forEach((element) => {
        if (this.selectedRegions.includes(element.id)) element.checked = true;
        else element.checked = false;
      });
    }
  }
  setCheckedSubGeographicalRegions() {
    var filter = localStorage.getItem('filter');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'Survey') return;
    if (filterObject.filter.filterParams.selectedSubgeographicalRegions) {
      this.selectedSubGeographicalRegions = filterObject.filter.filterParams.selectedSubgeographicalRegions;
      this.subGeographicalRegions.forEach((element) => {
        if (this.selectedSubGeographicalRegions.includes(element.id as number)) element.checked = true;
        else element.checked = false;
      });
    }
  }
  setCheckedGeographicalRegions() {
    var filter = localStorage.getItem('filter');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'Survey') return;
    if (filterObject.filter.filterParams.selectedGeographicalRegions) {
      let selectedGeographicalRegions = filterObject.filter.filterParams.selectedGeographicalRegions;
      this.geographicalRegions.forEach((element) => {
        if (selectedGeographicalRegions.includes(element.id)) element.checked = true;
        else element.checked = false;
      });
    }
  }

  setFilters() {
    var filter = localStorage.getItem('filter');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'Survey') return;
    this.currentPage = filterObject.filter.pageInfo.page;
    this.pageSize = filterObject.filter.pageInfo.pageSize;
    this.count = filterObject.filter.pageInfo.count;
    this.searchText = filterObject.filter.filterParams.search;
    this.filterByValue = filterObject.filter.filterParams.filterBy;
    this.selectedSearchType = filterObject.filter.filterParams.searchType;
    this.dateFrom = filterObject.filter.filterParams.dateFrom;
    this.dateTo = filterObject.filter.filterParams.dateTo;
    this.tariff = filterObject.filter.filterParams.tariffId
      ? filterObject.filter.filterParams.tariffId
      : 0;
    this.currentTab = filterObject.filter.filterParams.status;
    this.selectedAreas = filterObject.filter.filterParams.selectedAreas
      ? filterObject.filter.filterParams.selectedAreas
      : [];
    this.meterStatus = filterObject.filter.filterParams.meterStatus;
    this.customerPhase = filterObject.filter.filterParams.customerPhase;
    this.customerType = filterObject.filter.filterParams.customerType;
    this.selectedRegions = filterObject.filter.filterParams.selectedRegions
      ? filterObject.filter.filterParams.selectedRegions
      : [];
    this.selectedSubGeographicalRegions = filterObject.filter.filterParams.selectedSubgeographicalRegions
      ? filterObject.filter.filterParams.selectedSubgeographicalRegions
      : [];
    this.selectedOrganizationId = filterObject.filter.filterParams.organizationId
      ? filterObject.filter.filterParams.organizationId
      : 0;
    this.dueDateFilter = filterObject.filter.filterParams.dueDateFilter ?? -1;
    this.reopenStatus = filterObject.filter.filterParams.reopenStatus ?? 0;
    this.filterByName = this.filterByOptions[this.filterByValue];
      this.selectedSearchTypeDisplay =
      this.searchByOptions.find(
        (type) => type.name == filterObject.filter.filterParams.searchType
      )?.translation ?? this.searchByOptions[0].translation;
    localStorage.removeItem('filter');
  }
  removeFilterStorage() {
    if (this.tariffs.length > 0 &&
      ((this.areas.length > 0 && this.regions.length > 0) ||
        (this.subGeographicalRegions.length > 0 && this.geographicalRegions.length > 0)))
      localStorage.removeItem('filter');
  }
}
